import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { CalendarDays, CalendarRange, Clock, TrendingUp } from 'lucide-react';
import { trainerService } from '../../services';

/**
 * Trainer Schedule Page
 * Calendar view of trainer's assigned sessions
 */

const TrainerSchedulePage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month, week, list

  useEffect(() => {
    fetchSchedule();
  }, [currentDate]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await trainerService.getSchedule({ month, year });
      if (response.success) {
        setSessions(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get current month's calendar days
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty slots for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  // Get sessions for a specific date
  const getSessionsForDate = (date) => {
    if (!date) return [];
    return sessions.filter((session) => {
      const sessionDate = new Date(session.startTime);
      return (
        sessionDate.getDate() === date.getDate() &&
        sessionDate.getMonth() === date.getMonth() &&
        sessionDate.getFullYear() === date.getFullYear()
      );
    });
  };

  // Navigate month
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Get upcoming sessions grouped by date
  const getUpcomingSessions = () => {
    const upcoming = sessions
      .filter((s) => new Date(s.startTime) >= new Date())
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    const grouped = {};
    upcoming.forEach((session) => {
      const dateKey = new Date(session.startTime).toLocaleDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(session);
    });

    return grouped;
  };

  // Calculate stats
  const upcomingCount = sessions.filter((s) => s.status === 'scheduled').length;
  const todayCount = sessions.filter((s) => {
    const today = new Date().toDateString();
    return new Date(s.startTime).toDateString() === today;
  }).length;
  const thisWeekCount = sessions.filter((s) => {
    const sessionDate = new Date(s.startTime);
    const today = new Date();
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    return sessionDate >= today && sessionDate <= weekFromNow;
  }).length;
  const totalHours = sessions.reduce((sum, s) => {
    const start = new Date(s.startTime);
    const end = new Date(s.endTime);
    return sum + (end - start) / (1000 * 60 * 60);
  }, 0);

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const days = getDaysInMonth(currentDate);
  const upcomingSessions = getUpcomingSessions();

  return (
    <DashboardLayout title="Schedule">
      <div className="p-4 space-y-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/trainer/dashboard')}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors group"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-secondary-900">My Schedule</h1>
            <p className="text-sm text-neutral-600 mt-1">View and manage your training sessions</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={goToToday}>
              Today
            </Button>
            <div className="flex items-center gap-2 bg-neutral-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'month' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Today"       value={todayCount}             icon={CalendarDays}  variant="emerald" />
          <StatCard label="This Week"   value={thisWeekCount}          icon={CalendarRange} variant="blue" />
          <StatCard label="Upcoming"    value={upcomingCount}          icon={Clock}         variant="violet" />
          <StatCard label="Total Hours" value={`${totalHours.toFixed(0)}h`} icon={TrendingUp} variant="amber" />
        </div>

        {/* Calendar View */}
        {viewMode === 'month' && (
          <Card>
            <div className="p-6">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-neutral-900">{monthName}</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={previousMonth}
                    className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextMonth}
                    className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-neutral-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {days.map((date, index) => {
                  if (!date) {
                    return <div key={index} className="aspect-square" />;
                  }

                  const sessionsOnDate = getSessionsForDate(date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedDate(date)}
                      className={`aspect-square p-2 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : isToday
                          ? 'border-blue-300 bg-blue-50'
                          : sessionsOnDate.length > 0
                          ? 'border-neutral-200 bg-neutral-50 hover:border-primary-300'
                          : 'border-transparent hover:bg-neutral-50'
                      }`}
                    >
                      <div className="flex flex-col h-full">
                        <span
                          className={`text-sm font-medium mb-1 ${
                            isToday ? 'text-blue-600' : isSelected ? 'text-primary-600' : 'text-neutral-900'
                          }`}
                        >
                          {date.getDate()}
                        </span>
                        {sessionsOnDate.length > 0 && (
                          <div className="flex flex-col gap-0.5 mt-auto">
                            {sessionsOnDate.slice(0, 2).map((session) => (
                              <div
                                key={session._id}
                                className={`w-full h-1 rounded-full ${
                                  session.status === 'live'
                                    ? 'bg-green-500'
                                    : session.status === 'scheduled'
                                    ? 'bg-blue-500'
                                    : 'bg-neutral-400'
                                }`}
                              />
                            ))}
                            {sessionsOnDate.length > 2 && (
                              <span className="text-xs text-neutral-500">+{sessionsOnDate.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Selected Date Sessions */}
              {selectedDate && (
                <div className="mt-6 pt-6 border-t border-neutral-200">
                  <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-4">
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h3>
                  <div className="space-y-3">
                    {getSessionsForDate(selectedDate).length > 0 ? (
                      getSessionsForDate(selectedDate).map((session) => (
                        <div
                          key={session._id}
                          className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors cursor-pointer"
                          onClick={() => navigate(`/trainer/sessions/${session._id}`)}
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-1 h-12 rounded-full ${
                              session.status === 'live' ? 'bg-green-500' : session.status === 'scheduled' ? 'bg-blue-500' : 'bg-neutral-400'
                            }`} />
                            <div className="flex-1">
                              <h4 className="font-semibold text-neutral-900">{session.name}</h4>
                              <p className="text-sm text-neutral-600">
                                {new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} -{' '}
                                {new Date(session.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant={session.status}>{session.status}</Badge>
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-neutral-500 text-center py-8">No sessions scheduled for this date</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg sm:text-xl font-bold text-neutral-900 mb-6">Upcoming Sessions</h2>
              <div className="space-y-6">
                {Object.keys(upcomingSessions).length > 0 ? (
                  Object.entries(upcomingSessions).map(([dateKey, dateSessions]) => (
                    <div key={dateKey}>
                      <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3">
                        {new Date(dateSessions[0].startTime).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          timeZone: 'UTC',
                        })}
                      </h3>
                      <div className="space-y-3">
                        {dateSessions.map((session) => (
                          <div
                            key={session._id}
                            className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors cursor-pointer"
                            onClick={() => navigate(`/trainer/sessions/${session._id}`)}
                          >
                            <div className="flex items-center gap-4 flex-1">
                              <div className={`w-1 h-12 rounded-full ${
                                session.status === 'live' ? 'bg-green-500' : session.status === 'scheduled' ? 'bg-blue-500' : 'bg-neutral-400'
                              }`} />
                              <div className="flex-1">
                                <h4 className="font-semibold text-neutral-900">{session.name}</h4>
                                <div className="flex items-center gap-4 mt-1">
                                  <p className="text-sm text-neutral-600">
                                    {new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} -{' '}
                                    {new Date(session.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                                  </p>
                                  <p className="text-sm text-neutral-500">
                                    {session.currentParticipants}/{session.expectedParticipants || session.maxParticipants} participants
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant={session.status}>{session.status}</Badge>
                              <Button variant="ghost" size="sm">
                                View
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-full mb-4">
                      <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-neutral-500">No upcoming sessions</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TrainerSchedulePage;
